import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { getAllArticles } from "../../services/articleService";
import SectionHeading from "../public/SectionHeading";

const withTimeout = (promise, timeoutMs = 4500) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Article load timed out")), timeoutMs)
    ),
  ]);

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const navigate = useNavigate();

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      const allArticles = await withTimeout(getAllArticles());
      const publicArticles = allArticles
        .filter((article) => article.visibility === "public")
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setArticles(publicArticles);
    } catch (error) {
      console.error("Error loading articles:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  if (loading) {
    return (
      <section
        className="public-section public-section--tint"
        id="articles"
      >
        <div className="container">
          <SectionHeading
            eyebrow="Writing"
            title="Notes from the build"
            description="Occasional write-ups about systems, tools, and lessons learned along the way."
          />
          <div className="articles-skeleton">
            <div className="skeleton articles-skeleton-card" />
            <div className="articles-skeleton-lines">
              <div className="skeleton" />
              <div className="skeleton" />
              <div className="skeleton" />
              <div className="skeleton" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (articles.length === 0) return null;

  const categories = [
    "ALL",
    ...new Set(articles.map((article) => article.categoryLabel).filter(Boolean)),
  ].slice(0, 5);

  const filteredArticles =
    filter === "ALL"
      ? articles
      : articles.filter((article) => article.categoryLabel === filter);

  const visibleArticles = filteredArticles.slice(0, 6);
  const leadArticle = visibleArticles[0];
  const listArticles = visibleArticles.slice(1);

  const openArticle = (article) => {
    if (article?.slug) navigate(`/article/${article.slug}`);
  };

  return (
    <section className="public-section public-section--tint" id="articles">
      <div className="container">
        <SectionHeading
          eyebrow="Writing"
          title="Notes from the build"
          description="Occasional write-ups about systems, tools, and lessons learned along the way."
          aside={
            <span className="section-count">
              {articles.length} {articles.length === 1 ? "post" : "posts"}
            </span>
          }
        />

        {categories.length > 1 && (
          <div className="project-filters" aria-label="Filter articles">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={`filter-chip${
                  filter === category ? " is-active" : ""
                }`}
                aria-pressed={filter === category}
                onClick={() => setFilter(category)}
              >
                {category === "ALL" ? "All writing" : category}
              </button>
            ))}
          </div>
        )}

        {leadArticle && (
          <div
            className={`articles-layout${
              listArticles.length === 0 ? " articles-layout--solo" : ""
            }`}
          >
            <article
              className="article-lead"
              role="link"
              tabIndex={0}
              aria-label={`Read ${leadArticle.title}`}
              onClick={() => openArticle(leadArticle)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openArticle(leadArticle);
                }
              }}
            >
              {leadArticle.image && (
                <div className="article-lead-media">
                  <img
                    src={leadArticle.image}
                    alt={leadArticle.title}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
              <div className="article-lead-body">
                {leadArticle.categoryLabel && (
                  <span className="article-kicker">
                    {leadArticle.categoryLabel}
                  </span>
                )}
                <h3 className="article-lead-title">{leadArticle.title}</h3>
                {leadArticle.excerpt && (
                  <p className="article-lead-excerpt">
                    {leadArticle.excerpt}
                  </p>
                )}
                <div className="article-meta">
                  <span>{formatDate(leadArticle.date)}</span>
                  {leadArticle.readTime && (
                    <span>{leadArticle.readTime}</span>
                  )}
                  <span className="article-read">
                    Read article
                    <ArrowUpRight size={14} />
                  </span>
                </div>
              </div>
            </article>

            {listArticles.length > 0 && (
              <div className="article-side-list">
                {listArticles.map((article) => (
                  <button
                    key={article.id}
                    type="button"
                    className="article-list-item"
                    onClick={() => openArticle(article)}
                  >
                    <span className="article-list-date">
                      {formatDate(article.date)}
                    </span>
                    <span className="article-list-copy">
                      <span className="article-list-title">
                        {article.title}
                      </span>
                      {article.categoryLabel && (
                        <span className="article-list-category">
                          {article.categoryLabel}
                        </span>
                      )}
                    </span>
                    <ArrowUpRight
                      className="article-list-arrow"
                      size={15}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {filteredArticles.length === 0 && (
          <div className="project-empty">
            <h3>No writing in this category yet</h3>
            <p>Try another topic to see more posts.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Articles;
